import { useState, useEffect, useRef, useCallback } from "react";
import debounce from "lodash/debounce";
import { getAutocompleteSuggestions } from "../utils/search-utils/getAutocompleteSuggestions";
import type { SuggestionGroup } from "@bmi-digital/components/search";
import type { GetMicroCopy } from "../components/MicroCopy";

interface DebounceFetchParams {
  term: string;
  microCopyFn: GetMicroCopy;
  country: string;
  service: typeof getAutocompleteSuggestions;
  variantCodeToPathMap?: Record<string, string>;
}

export const useAutocomplete = (
  getMicroCopy: GetMicroCopy,
  countryCode: string,
  searchService = getAutocompleteSuggestions,
  variantCodeToPathMap?: Record<string, string>
) => {
  const [suggestions, setSuggestions] = useState<SuggestionGroup[]>([]);

  const activeTermRef = useRef<string | null>(null);

  const fetchSuggestionsRef = useRef(
    debounce(
      async ({
        term,
        microCopyFn,
        country,
        service,
        variantCodeToPathMap
      }: DebounceFetchParams) => {
        const results = await service(
          term,
          microCopyFn,
          country,
          variantCodeToPathMap
        );

        if (activeTermRef.current === term) {
          setSuggestions(results);
        }
      },
      300
    )
  );

  const fetchSuggestions = useCallback(
    (term: string) => {
      activeTermRef.current = term;
      if (!term) {
        fetchSuggestionsRef.current.cancel();
        setSuggestions([]);
        return;
      }
      fetchSuggestionsRef.current({
        term,
        microCopyFn: getMicroCopy,
        country: countryCode,
        service: searchService,
        variantCodeToPathMap
      });
    },
    [getMicroCopy, countryCode, searchService, variantCodeToPathMap]
  );

  useEffect(() => {
    return () => {
      fetchSuggestionsRef.current.cancel();
    };
  }, []);

  return { suggestions, fetchSuggestions };
};
